import { Add, Delete, Edit, PlaylistAdd } from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import { useState } from "react";
import { ConfirmacaoAcaoModal } from "../../../core/components/ConfirmacaoAcaoModal";
import { useBandeirasCartao } from "../../hooks/useBandeirasCartao";
import { useFetchBandeirasCartao } from "../../hooks/useFetchBandeirasCartao";
import { IBandeiraCartao } from "../../interface/IBandeiraCartao";

const BANDEIRA_VAZIA: IBandeiraCartao = { nome: '', ativo: true };

/** Cadastro das bandeiras aceitas. O nome é o que aparece no relatório de conciliação. */
export const BandeirasCartao = () => {
    const { bandeiras, isLoading, mutate } = useFetchBandeirasCartao();
    const { criar, atualizar, deletar, adicionarPadroes, loading } = useBandeirasCartao();

    const [formularioAberto, setFormularioAberto] = useState(false);
    const [bandeira, setBandeira] = useState<IBandeiraCartao>(BANDEIRA_VAZIA);
    const [bandeiraExcluindo, setBandeiraExcluindo] = useState<IBandeiraCartao | null>(null);

    const abrirNova = () => {
        setBandeira(BANDEIRA_VAZIA);
        setFormularioAberto(true);
    };

    const abrirEdicao = (selecionada: IBandeiraCartao) => {
        setBandeira(selecionada);
        setFormularioAberto(true);
    };

    const fecharFormulario = () => {
        setFormularioAberto(false);
        setBandeira(BANDEIRA_VAZIA);
    };

    const handleSalvar = async () => {
        if (!bandeira.nome?.trim()) return;

        const resultado = bandeira.uuid
            ? await atualizar(bandeira.uuid, bandeira)
            : await criar(bandeira);

        if (resultado) {
            fecharFormulario();
            mutate();
        }
    };

    const handleExcluir = async () => {
        if (!bandeiraExcluindo?.uuid) return;

        if (await deletar(bandeiraExcluindo.uuid)) {
            setBandeiraExcluindo(null);
            mutate();
        }
    };

    const handleAdicionarPadroes = async () => {
        if (await adicionarPadroes()) {
            mutate();
        }
    };

    return (
        <div style={{ maxWidth: '95%', margin: '0 auto', paddingTop: '1rem' }}>
            <Paper variant="outlined" sx={{ padding: '1rem' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6">Bandeiras de cartão</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" startIcon={<PlaylistAdd />} onClick={handleAdicionarPadroes} disabled={loading}>
                            Adicionar bandeiras padrão
                        </Button>
                        <Button variant="contained" startIcon={<Add />} onClick={abrirNova}>
                            Nova bandeira
                        </Button>
                    </Box>
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell align="center">Situação</TableCell>
                                <TableCell align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center"><CircularProgress size={24} /></TableCell>
                                </TableRow>
                            )}
                            {!isLoading && !bandeiras.length && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                            Nenhuma bandeira cadastrada. Use "Adicionar bandeiras padrão" para começar.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {bandeiras.map(item => (
                                <TableRow key={item.uuid}>
                                    <TableCell>{item.nome}</TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            size="small"
                                            label={item.ativo ? "Ativa" : "Inativa"}
                                            color={item.ativo ? "success" : "default"}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Button size="small" startIcon={<Edit />} onClick={() => abrirEdicao(item)}>
                                            Editar
                                        </Button>
                                        <Button size="small" color="error" startIcon={<Delete />} onClick={() => setBandeiraExcluindo(item)}>
                                            Excluir
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={formularioAberto} onClose={fecharFormulario} maxWidth="xs" fullWidth>
                <DialogTitle>{bandeira.uuid ? "Editar bandeira" : "Nova bandeira"}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField
                            size="small"
                            label="Nome"
                            fullWidth
                            required
                            value={bandeira.nome ?? ''}
                            onChange={(e) => setBandeira(prev => ({ ...prev, nome: e.target.value }))}
                            inputProps={{ maxLength: 60 }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={bandeira.ativo ?? true}
                                    onChange={(e) => setBandeira(prev => ({ ...prev, ativo: e.target.checked }))}
                                    color="success"
                                />
                            }
                            label="Ativa"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={fecharFormulario}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSalvar} disabled={!bandeira.nome?.trim() || loading}>
                        {loading ? <CircularProgress size={20} /> : "Salvar"}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmacaoAcaoModal
                open={!!bandeiraExcluindo}
                titulo="Excluir bandeira"
                descricao={`Deseja excluir "${bandeiraExcluindo?.nome}"? Os pagamentos já registrados com ela continuam intactos no relatório.`}
                onConfirmar={handleExcluir}
                onCancelar={() => setBandeiraExcluindo(null)}
                loading={loading}
            />
        </div>
    );
};
